/*!
 * refs.js — one place that answers "does this reference resolve?", and says so
 * out loud when it does not.
 * © 2025 Rob Canning — GPLv3
 *
 * Oscilla scores are full of one element naming another: trig:<uid> for object
 * triggers, freq:fader1.t for signal bindings, to:[idA] waypoints, path:myPath,
 * nav target:markName. Before this module, a name that pointed at nothing
 * behaved differently in every case — probe triggers and signal bindings failed
 * completely silently and forever, trans and ui warned, nav hid the miss behind
 * a debug flag, and nothing anywhere threw. A typo in a uid produced a score
 * that loaded cleanly and simply did less than it should.
 *
 * WHY REPORTING IS DEFERRED
 * Resolution cannot happen when the reference is read. assignCues walks the
 * tree in document order, so an element may legitimately name a uid that is
 * registered later in the same walk. Each site therefore *notes* its reference
 * and supplies a resolver; nothing is judged until reportUnresolvedRefs() runs
 * after the walk is complete. A reference that resolves by then was never a
 * problem, whatever the document order.
 *
 * Usage from a cue module:
 *
 *     import { noteRef } from "../control/refs.js";
 *     noteRef({ kind: "trig", name: uid, el, cueExpr });
 *
 * and, once per load, after the tree walk:
 *
 *     import { reportUnresolvedRefs } from "../control/refs.js";
 *     reportUnresolvedRefs();
 */

/** Channels any publisher is known to emit. Union of the publish() call sites —
 *  see notes/control-plane-inventory.md §B. Used only to catch a misspelt
 *  channel (fader1.tt); an unknown name is a warning, never a hard failure,
 *  because an ext() extension may publish anything it likes. */
import { onReset } from "../system/teardown.js";

const KNOWN_CHANNELS = new Set([
  "t", "v", "x", "y", "z", "p",                        // o2p, oscCtrl, controlXY
  "legT",                                              // trans
  "index",                                             // image
  "beat", "phase", "bar",                              // metro
  "time",                                              // video
  "elapsed", "remaining", "end",                       // stopwatch
  "speed", "playing",                                  // transport
  "bpm", "zoom",                                       // beat, view (liveSignals.js)
  "vel", "vx", "vy", "tvel",                           // velocityChannels()
  "angle", "rad", "norm", "sin", "cos",                // rotate
  "sx", "sy", "uniform",                               // scale
  "hNorm", "sNorm", "lNorm",                           // color
  "opacity",                                           // fade
  "azi", "elev", "dist",                               // spatial
  "amp", "peak", "centroid", "onset",                  // audio analysis
  "low", "mid", "high", "pitch", "note", "pitchConf",
  "value",                                             // random (norm is shared with rotate)
  "level", "rms", "fader", "env", "hit", "n", "freq",  // a sound, a synth (liveSignals.js, synth.js)
  "motion", "bright",                                  // the camera (cam.js; x, y, level, hit, n above)
  "down",                                              // the mouse (liveSignals.js; x, y, hit, n above)
]);

/** Human wording per reference kind, used in the report. */
const KIND_LABEL = {
  trig:     "trig:<uid> — object trigger source",
  signal:   "signal binding source",
  waypoint: "trans() waypoint",
  path:     "o2p() path",
  handle:   "o2p() rotation handle",
  target:   "target element",
  mark:     "rehearsal mark",
  reuse:    "reuse() block",
  ext:      "ext() function",
};

let _notes = [];
let _lastReport = [];

/**
 * Resolve a uid to its element, the way every cue module does it.
 *
 * findElementByUid lives in cues/animShared.js, which pulls the whole overlay
 * stack in behind it — far too much for a control-plane module to depend on,
 * and unloadable outside a browser. It is reached lazily through the injection
 * below instead, so refs.js stays a leaf.
 */
let _findElementByUid = null;

/** Let the cue layer supply its uid lookup without refs.js importing it. */
export function setUidResolver(fn) {
  _findElementByUid = typeof fn === "function" ? fn : null;
}

/**
 * Sources that exist without an element behind them.
 *
 * Every other signal source is a cue on some element, so "does this uid resolve"
 * is answered by looking the element up. These two are published by the system
 * itself — the transport from the animation loop, mic from the audio analyser —
 * and resolve to no element at all. Without this, follow(transport.t, …) was
 * reported as a broken reference in every score that used it, which is exactly
 * the noise this module exists to avoid making.
 */
// "adc" is the audio input (adc() cue, after Pd/Max's adc~). "mic" stays
// recognised so an older score that named it is not reported as broken.
// "mic" is deliberately NOT here. It was this source's uid before the adc()
// cue; listing it would make a stale mic.amp resolve and then never fire,
// which is the exact silent failure this module exists to surface.
// beat, view and page: liveSignals.js (the tempo grid, the zoom, page turns). cam: the camera (cam.js).
// midi, and midi1..midi16 for one channel: midiIn.js.
export const SYSTEM_SOURCES = new Set(["transport", "adc", "cam", "mouse", "beat", "view", "page", "midi"]);
const MIDI_CHANNEL_SOURCE = /^midi([1-9]|1[0-6])$/;

export function isSystemSource(uid) {
  const s = String(uid ?? "");
  return SYSTEM_SOURCES.has(s) || MIDI_CHANNEL_SOURCE.test(s);
}

export function resolveUid(uid) {
  if (!uid) return null;
  // An element wins over the system-source fallback. adc() can be placed on the
  // canvas as a mic object, and when it is, "adc" IS an element — the patch view
  // needs that element to hang the outlets on. Checking SYSTEM_SOURCES first
  // would hand back a bare true and the mic would draw as a dangling cord.
  const fromRegistry = window.oscillaAnimRegistry?.[uid]?.el;
  if (fromRegistry) return fromRegistry;
  const found = _findElementByUid?.(uid)?.el || null;
  if (found) return found;
  if (isSystemSource(uid)) return true;      // real, but not an element
  return null;
}

/**
 * Open a scan. Called at the top of assignCues.
 *
 * Deliberately NOT a reset. assignCues runs more than once per session — a
 * page swap calls it again for the new page (page.js), and a rescan calls it
 * for the same root. Clearing here would make a page-mode score report only
 * whichever tree happened to be assigned last, and the main score's references
 * would go unchecked. Instead notes accumulate and stale ones age out: an entry
 * whose element has left the document belongs to a page that is gone.
 */
export function beginRefScan() {
  _notes = _notes.filter((n) => !n.el || n.el.isConnected);
}

/**
 * Drop every note belonging to one element — for the same reason as
 * dropEdgesFor(): a rewritten element keeps its node, so a reference that was
 * just deleted would still be reported as broken.
 */
export function dropRefsFor(el) {
  if (!el) return 0;
  const before = _notes.length;
  _notes = _notes.filter((n) => n.el !== el);
  return before - _notes.length;
}

/** Forget everything. For a project change, and for tests. */
export function resetRefs() {
  _notes = [];
  _lastReport = [];
}
onReset("project", resetRefs);          // the cross-element report belongs to the score that made it

/**
 * Record a reference to be checked once the walk is finished.
 *
 * @param {object}   spec
 * @param {string}   spec.kind     one of KIND_LABEL's keys
 * @param {string}   spec.name     what was written in the score
 * @param {Element} [spec.el]      the element that wrote it
 * @param {string}  [spec.cueExpr] the full cue expression, for the report
 * @param {Function}[spec.resolve] () => truthy when the reference resolves.
 *                                 Defaults to resolving `name` as a uid.
 * @param {string}  [spec.note]    extra detail shown alongside a failure
 */
export function noteRef(spec) {
  if (!spec?.name) return;
  // Re-scanning the same tree must not multiply the report.
  for (const n of _notes) {
    if (n.kind === spec.kind && n.name === spec.name && n.el === spec.el) {
      n.resolve = spec.resolve ?? n.resolve;
      n.note = spec.note ?? n.note;
      return;
    }
  }
  _notes.push(spec);
}

/**
 * Note a parsed signalRef's source, and check its channel spelling.
 * Separated from noteRef because the channel half has its own failure mode:
 * the uid can exist while the channel is a typo, which resolves to a path
 * nobody ever writes — silent in exactly the same way.
 */
/**
 * The cue's own name, from a DSL id like `scale(uid:b1, …)`. Kept local so
 * refs.js does not have to import the dispatcher.
 */
function cueNameFromExpr(expr) {
  const m = /^\s*(?:cue:)?([a-zA-Z][a-zA-Z0-9]*)\s*\(/.exec(String(expr ?? ""));
  return m ? m[1] : null;
}

/**
 * Is `param` something this cue actually reads?
 *
 * follow() and map() are explicit, so the edge builder deliberately has no
 * allowlist of bindable names — anything can be bound to anything. The cost is
 * that a typo, or a plausible-but-wrong name, builds a perfectly good edge to a
 * parameter nothing looks at, and the cue simply never moves.
 *
 * Two real cases from writing demo-adc: fade() publishes `opacity` but does not
 * read it (its inlets are from/to/dur), and rotate() has no `speed` inlet at
 * all — only `dur`. Both bound cleanly and both did nothing, which took a
 * running score and a report from the user to notice.
 *
 * ports.js already knows the answer, so ask it. Unknown cue names and cues with
 * no port spec return null: "no opinion", never a warning.
 */
function unknownInletNote(cueExpr, param) {
  if (!param) return "";
  const name = cueNameFromExpr(cueExpr);
  const spec = name ? _portsFor?.(name) : null;
  if (!spec?.ins) return "";
  const ins = Object.keys(spec.ins);
  if (!ins.length || ins.includes(param)) return "";
  return `${name}() does not read "${param}:" — it reads ${ins.join(", ")}`;
}

// ports.js imports nothing from here, but keeping the dependency injected
// matches setUidResolver and keeps this module loadable on its own in tests.
let _portsFor = null;
export function setPortsResolver(fn) {
  if (typeof fn === "function") _portsFor = fn;
}

export function noteSignalRef(ref, el, cueExpr, param = null) {
  if (!ref?.source) return;
  const inletNote = unknownInletNote(cueExpr, param);
  const channelNote = isKnownChannel(ref.channel) ? "" : `unknown channel ".${ref.channel}"`;
  noteRef({
    kind: "signal",
    name: `${ref.source}.${ref.channel}`,
    el, cueExpr,
    resolve: () => resolveUid(ref.source),
    // The inlet problem is the more actionable of the two, so it leads.
    note: inletNote || channelNote,
  });
}

/**
 * Resolve everything noted this scan and report what did not.
 * Returns the unresolved entries, so a caller (the graph view, later) can draw
 * them as dangling rather than only printing them.
 */
export function reportUnresolvedRefs({ silent = false } = {}) {
  const bad = [];
  const suspect = [];

  for (const n of _notes) {
    const ok = n.resolve ? !!n.resolve() : !!resolveUid(n.name);
    const row = {
      kind: n.kind,
      name: n.name,
      cue: n.cueExpr ? String(n.cueExpr).slice(0, 80) : "",
      element: n.el?.id ? String(n.el.id).slice(0, 60) : (n.el?.tagName ?? ""),
      detail: n.note || "",
    };
    if (!ok) bad.push(row);
    else if (n.note) suspect.push(row);
  }

  _lastReport = [...bad, ...suspect];

  if (!silent && (bad.length || suspect.length)) {
    const noun = (n) => (n === 1 ? "reference" : "references");
    console.group(
      `%c[refs] ${bad.length} unresolved ${noun(bad.length)}` +
      (suspect.length ? `, ${suspect.length} suspect` : "") +
      " — these do nothing at all",
      "color:#c33;font-weight:bold"
    );
    for (const r of bad) {
      console.warn(
        `✗ ${KIND_LABEL[r.kind] ?? r.kind}: "${r.name}" matches no element` +
        (r.detail ? ` (${r.detail})` : ""),
        r.cue ? `\n    in: ${r.cue}` : ""
      );
    }
    for (const r of suspect) {
      console.warn(`? ${KIND_LABEL[r.kind] ?? r.kind}: "${r.name}" — ${r.detail}`, r.cue ? `\n    in: ${r.cue}` : "");
    }
    console.groupEnd();
  }

  window.oscillaUnresolvedRefs = _lastReport;
  return _lastReport;
}

/** The last report, for anything that wants to render it rather than log it. */
export function getUnresolvedRefs() {
  return _lastReport;
}

/** Exposed for the live console and for in-page tests. */
export function isKnownChannel(channel) {
  return KNOWN_CHANNELS.has(channel)
    || /^(hit\d+|div\d+(e\d+)?|band\d+of\d+)$/.test(channel)   // every(4, x.hit), beat.div(4), every(3, beat.div(4)), x.band(2, 4)
    || /^(note|cc|pc)\d+$/.test(channel)                       // midi.note60, midi3.cc64, midi.pc3
    || /^[\w-]+\.(x|y|p|azi|elev|dist)$/.test(channel);        // a controlXY handle's own: pad1.h1.x
}

window.oscillaRefs = { resolveUid, isSystemSource, setUidResolver, reportUnresolvedRefs, getUnresolvedRefs, isKnownChannel, resetRefs };
